import { LlmConversationOverlayComponent } from '@adaptiva/ai/widgets';
import { Destroyer } from '@adaptiva/core/common';
import { LogicalAnd, StringEquals } from '@adaptiva/core/filters';
import { OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { AuthenticationService, EJavaClassNames, IObjectTableListConfiguration, ObjectManagerService } from '@adaptiva/data-service';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { LlmConversation } from '../../classes';
import { ILlmConversation } from '../../interfaces';
import { AidaService } from '../aida';

@Injectable()
export class LlmConversationService extends Destroyer {

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly overlayService: OverlayService = inject(OverlayService);

	protected readonly objectManager: ObjectManagerService = inject(ObjectManagerService);

	protected readonly auth: AuthenticationService = inject(AuthenticationService);

	protected readonly snackbars: SnackbarService = inject(SnackbarService);

	public conversationOverlay: WritableSignal<LlmConversationOverlayComponent> = signal(null);

	public open(_conversation: LlmConversation): void {
		this.aida.conversation.set(_conversation);
		this.conversationOverlay()?.close();
	}

	public renameChat(_conversation: ILlmConversation): Observable<boolean> {
		return this.overlayService.prompt({
			title: 'Rename Chat',
			label: 'Enter a new name for this chat session',
			validators: [(_name: string) => {
				const _isValidName: boolean = !!_name;

				if (!_isValidName)
					this.snackbars.display('Please supply a new Chat name');

				return _isValidName;
			}],
			type: 'text'
		}).pipe(
			this.takeOneUntilDestroyed(),
			switchMap((_newName: string) => {
				return this.aida.renameConversation({
					conversationId: _conversation.objectId,
					newName: _newName
				});
			})
		);
	}

	public deleteChat(_conversation: ILlmConversation): Observable<ILlmConversation> {
		return this.overlayService.confirm(
			'Delete Chat?',
			'This chat will be permanently deleted and cannot be recovered. \nAre you sure you want to continue?'
		).pipe(
			this.takeOneUntilDestroyed(),
			switchMap((_isConfirmed: boolean) => {
				if (_isConfirmed)
					return this.objectManager.deleteObjectById<ILlmConversation>(EJavaClassNames.LlmConversation, _conversation.objectId);

				return EMPTY;
			})
		);
	}

	public conversationQueryConfig(): IObjectTableListConfiguration {
		return {
			ascending: false,
			objectClassName: EJavaClassNames.LlmConversation,
			offset: 0,
			parentFolderId: 0,
			limit: 5,
			sortColumn: 'lastModificationTime',
			objectFilter: new LogicalAnd([
				new StringEquals('createdBy', this.auth.user?.value?.loginId)
			])
		};
	}

}
